import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Row, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Select from 'react-select';
import { QaService } from "./service";
import { QaCategoryService } from "../qaCategories/service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Editor } from "@tinymce/tinymce-react";
import { useTranslation } from "react-i18next";
import FormErrorSummary from "../../shared/components/FormErrorSummary";

const schema = yup.object().shape({
    category: yup.number().required('formErrors.categoryRequired').typeError('formErrors.categoryRequired'),
    question: yup.string().required('formErrors.questionRequired'),
    answer: yup.string().required('formErrors.answerRequired')
});

interface QaState {
    category: number;
    question: string;
    answer: string;
}

const QaForm: React.FC = () => {
    const { t } = useTranslation(); // Initialize translation hook
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);
    const [qaData, setQaData] = useState<QaState | null>(null);
    const [categories, setCategories] = useState([]);
    const navigate = useNavigate();
    const { control, handleSubmit, reset, formState: { errors } } = useForm<QaState>({
        resolver: yupResolver(schema),
        defaultValues: {
            category: undefined,
            question: "",
            answer: ""
        }
    });

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await QaCategoryService.getAll({ perPage: 0, page: 1 });
                setCategories(response.data.map((cat: any) => ({ label: cat.title, value: cat.id })));
            } catch (error) {
                ErrorToast(error);
            }
        };

        fetchCategories();
    }, []);

    useEffect(() => {
        if (isEdit) {
            const fetchQa = async () => {
                try {
                    const response = await QaService.getSingle(+id!);
                    const qa = response;

                    setQaData({
                        category: qa.category.id,
                        question: qa.question,
                        answer: qa.answer
                    });
                } catch (error) {
                    ErrorToast(error);
                }
            };

            fetchQa();
        }
    }, [isEdit, id]);

    useEffect(() => {
        if (qaData) {
            reset(qaData);
        }
    }, [qaData, reset]);

    const onSubmit = async (data: QaState) => {
        try {
            isEdit
                ? await QaService.update(data, +id!)
                : await QaService.create(data);

            SuccessToast(t(`qa.${isEdit ? 'successUpdate' : 'successCreate'}`));
            navigate('/qas');
            reset();
        } catch (error) {
            ErrorToast(error);
        }
    };

    return (
        <React.Fragment>
            <Row>
                <Col sm={12}>
                    <Card>
                        <CardBody>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <FormErrorSummary errors={errors} />
                                <Row className="g-3">
                                    <Col sm={12} xl={6}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("qa.question")}</label>
                                            <Controller
                                                name="question"
                                                control={control}
                                                render={({ field }) => (
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errors.question ? 'is-invalid' : ''}`}
                                                        placeholder={t("qa.enterQuestion")}
                                                        {...field}
                                                    />
                                                )}
                                            />
                                            {errors.question && <div className="invalid-feedback">
                                                {t(errors.question.message as string)}
                                            </div>}
                                        </div>
                                    </Col>
                                    <Col sm={6}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("qa.category")}</label>
                                            <Controller
                                                name="category"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        id="category"
                                                        classNamePrefix="react-select"
                                                        value={categories.find((cat: any) => cat.value === field.value)}
                                                        onChange={(selectedOption: any) => field.onChange(selectedOption?.value)}
                                                        options={categories}
                                                    />
                                                )}
                                            />
                                            {errors.category && <div className="invalid-feedback d-block">
                                                {t(errors.category.message as string)}
                                            </div>}
                                        </div>
                                    </Col>
                                    <Col sm={12}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("qa.answer")}</label>
                                            <div className={errors.answer ? 'is-invalid' : ''}>
                                                <Controller
                                                    name="answer"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Editor
                                                            apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                                                            value={field.value}
                                                            onEditorChange={(content: string) => field.onChange(content)}
                                                            init={{
                                                                height: 360,
                                                                menubar: false,
                                                                branding: false,
                                                                plugins: [
                                                                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                                                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                                                    'insertdatetime', 'media', 'table', 'help', 'wordcount',
                                                                ],
                                                                toolbar:
                                                                    'undo redo | blocks | bold italic underline strikethrough | ' +
                                                                    'alignleft aligncenter alignright alignjustify | ' +
                                                                    'bullist numlist outdent indent | table | link image | removeformat | code',
                                                                content_style: 'body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; }',
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            {errors.answer && <div className="invalid-feedback">
                                                {t(errors.answer.message as string)}
                                            </div>}
                                        </div>
                                    </Col>
                                    <Col sm={12}>
                                        <Button type="submit" variant="primary">
                                            {t(`qa.${isEdit ? 'update' : 'create'}`)}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default QaForm;